import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import './style.scss';
import FormButton from 'components/Common/FormButton';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { useQueryParams } from 'components/Navigation/NavStack';
import DefaultPatientTextField from 'components/Utils/DefaultPatientTextField';
import { setCredentialsSchema, SetCredentialsValues } from 'types/forms';
import { getTenantIdentity } from 'hooks/useTenantIdentity';
import TenantImg from '../../components/TenantImg';

const NewCredentials = () => {
  const dispatch = useAppDispatch();
  const masterNav = useNavStack(Stacks.Master);

  const [smsCode, setSmsCode] = useState<string>('');
  const [patientId, setPatientId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

  const { auth, id } = useQueryParams() as { auth?: string; id?: string };

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<SetCredentialsValues>({
    resolver: zodResolver(setCredentialsSchema),
    defaultValues: { username: '', password: '', password2: '', admin_code: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (auth) setSmsCode(auth);
    if (id) setPatientId(id);
  }, [auth, id]);

  async function onSubmitForm(values: SetCredentialsValues) {
    setLoading(true);
    const newCredentials = {
      username: values.username,
      password: values.password,
      admin_code: values.admin_code,
      sms_code: smsCode,
    };
    const result = await dispatch(
      api.Authentication.setCredentials({ patientId, newCredentials }),
      {
        errorTitle: 'Set Credentials',
        meta: true,
      },
    );
    if (result.isSuccess) {
      setSubmitSuccess(true);
    }
    setLoading(false);
  }

  function goToHome() {
    masterNav.popToRoot();
  }

  return (
    <div className="page new-credentials-screen">
      <div className="page-content padded-box">
        <TenantImg alt={getTenantIdentity().name} src="logo_color" width="230" className="logo" />

        {submitSuccess && (
          <>
            <span className="center-text">
              Your credentials are now active! You can login using our iOS/Android app, or using our
              web portal.
            </span>
            <center>
              <DefaultButton name="Sign In" onClick={goToHome} style={{ marginTop: '20px' }} />
            </center>
          </>
        )}
        {!submitSuccess && (
          <>
            <span className="center-text description">
              Below you can set the credentials for your account using the code provided by our
              staff.
            </span>

            <form className="link-account-form" onSubmit={handleSubmit(onSubmitForm)}>
              <DefaultPatientTextField
                name="username"
                control={control}
                label="Username"
                autoFocus
              />
              <DefaultPatientTextField
                name="password"
                control={control}
                label="Password"
                type="password"
              />
              <DefaultPatientTextField
                name="password2"
                control={control}
                label="Confirm Password"
                type="password"
              />
              <DefaultPatientTextField
                name="admin_code"
                control={control}
                label="Authentication Code"
              />
              <FormButton name="Set Credentials" disabled={!isValid} loading={loading} />
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default NewCredentials;
